import { fetchEngine } from "./fetchEngine";
import { fetchLog } from "./replicationControl";
import { invalidateLogListing } from "./useLogsSync";

/**
 * The imperative surface of the log-data acquisition subsystem — the minimal
 * set of non-hook entry points consumed outside `log_data`. Membership test:
 * a verb belongs here iff a human or external event issues it (*invalidate*
 * verbs, user commands); a verb another layer needs to run a mechanism is a
 * mis-homed mechanism. There is no *initialize* verb — the subsystem wires
 * itself lazily on first activation, reading the api from app_config.
 * Everything else consumers do is declarative hook subscription (see
 * apps/inspect/design/domain-ownership.md). Every verb is fire-and-forget —
 * consequences are observed through the data hooks, never awaited. Growing
 * this interface is a design decision, not a convenience.
 */
export interface ImperativeLogData {
  /** Re-fetch one log's details (toolbar refresh / edit-save) — the
   *  log-detail *invalidate* verb. */
  invalidateLogDetail(logDir: string, logFile: string | undefined): void;
  /** Invalidate the listing-sync queries (external freshness events / user
   *  refresh) — the listing *invalidate* verb. */
  invalidateLogListing(): void;
  /** Clear all locally persisted log data (database + cache), then request a
   *  listing re-sync so mounted panels re-acquire instead of sitting empty
   *  until the next poll tick — user-initiated maintenance from the viewer
   *  options popover. */
  clearData(): void;
}

export const imperativeLogData: ImperativeLogData = {
  invalidateLogDetail: (logDir, logFile) => {
    if (logFile === undefined) return;
    // fresh: true bypasses the read-through's cached-row shortcut — the
    // caller (toolbar refresh / edit-save) knows the row is stale.
    void fetchLog(logDir, logFile, { fresh: true }).catch(() => {});
  },
  invalidateLogListing,
  clearData: () => {
    fetchEngine.clearData();
    invalidateLogListing();
  },
};
